(() => {
  if (window.__diggnHomeStoryBooted) return;
  window.__diggnHomeStoryBooted = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  const stories = new Set();
  let frameRequested = false;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const hydrateVideo = (video) => {
    if (!video || video.dataset.hydrated === 'true' || reducedMotion.matches || saveData) return;
    const mobileSource = video.dataset.srcMobile;
    const source = window.matchMedia('(max-width: 749px)').matches && mobileSource
      ? mobileSource
      : video.dataset.src;
    if (!source) return;
    video.dataset.hydrated = 'true';
    const markReady = () => video.classList.add('is-video-ready');
    if (video.readyState >= 2) {
      markReady();
    } else {
      video.addEventListener('loadeddata', markReady, { once: true });
    }
    video.src = source;
    video.load();
  };

  const hydrateMedia = (container) => {
    if (!container) return;

    container.querySelectorAll('[data-dg-deferred-srcset]').forEach((source) => {
      const srcset = source.dataset.dgDeferredSrcset;
      if (!srcset || source.dataset.hydrated === 'true') return;
      source.dataset.hydrated = 'true';
      source.srcset = srcset;
    });

    container.querySelectorAll('[data-dg-deferred-src]').forEach((image) => {
      const source = image.dataset.dgDeferredSrc;
      if (!source || image.dataset.hydrated === 'true') return;
      image.dataset.hydrated = 'true';
      image.src = source;
    });
  };

  const scrubVideo = (video, progress) => {
    if (!video) return;
    const scrubProgress = clamp(progress, 0, 1);
    video.dataset.scrubProgress = scrubProgress.toFixed(5);
    video.pause();
    if (video.dataset.hydrated !== 'true' || video.readyState < 1 || !Number.isFinite(video.duration)) return;

    const target = scrubProgress * Math.max(video.duration - (1 / 30), 0);
    if (Math.abs(video.currentTime - target) < (1 / 48)) return;
    try { video.currentTime = target; } catch (error) {}
  };

  const setStoryIndex = (story, nextIndex) => {
    if (story.activeIndex !== nextIndex) {
      story.activeIndex = nextIndex;

      story.scenes.forEach((scene, index) => scene.classList.toggle('is-active', index === nextIndex));
      story.chapters.forEach((chapter, index) => {
        const isActive = index === nextIndex;
        chapter.classList.toggle('is-active', isActive);
        chapter.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
      story.truthCards.forEach((card, index) => card.classList.toggle('is-active', index === nextIndex));
      story.steps.forEach((step, index) => step.classList.toggle('is-active', index === nextIndex));
    }

    if (story.inView) {
      hydrateMedia(story.scenes[nextIndex]);
      hydrateMedia(story.chapters[nextIndex]);
    }

    story.videos.forEach((video) => {
      const videoIndex = Number(video.dataset.storyVideoIndex || 0);
      if (videoIndex === nextIndex && story.inView) {
        hydrateVideo(video);
      }
      video.pause();
    });
  };

  const scrubStoryScene = (story, index, progress) => {
    if (!story.inView) return;
    const activeVideo = story.videos.find((video) => Number(video.dataset.storyVideoIndex || 0) === index);
    if (!activeVideo) return;
    hydrateVideo(activeVideo);
    scrubVideo(activeVideo, progress);
  };

  const updateStory = (story) => {
    if (!story.root.isConnected) {
      stories.delete(story);
      return;
    }

    const rect = story.root.getBoundingClientRect();
    const viewport = Math.max(window.innerHeight || 0, 1);
    const travel = Math.max(rect.height - viewport, 1);
    const progress = clamp(-rect.top / travel, 0, 0.999999);
    const nextIndex = Math.min(story.count - 1, Math.floor(progress * story.count));
    const sceneProgress = clamp((progress * story.count) - nextIndex, 0, 1);
    story.root.style.setProperty('--dg-story-progress', progress.toFixed(4));
    story.root.style.setProperty('--dg-story-image-y', `${((0.5 - sceneProgress) * 3.6).toFixed(3)}%`);
    story.root.style.setProperty('--dg-story-image-scale', (1.045 - sceneProgress * 0.025).toFixed(4));
    setStoryIndex(story, nextIndex);
    scrubStoryScene(story, nextIndex, sceneProgress);
  };

  const updateAll = () => {
    frameRequested = false;
    stories.forEach(updateStory);
  };

  const requestUpdate = () => {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(updateAll);
  };

  const prepareStory = (root) => {
    if (!root || root.dataset.storyPrepared === 'true') return;
    root.dataset.storyPrepared = 'true';

    const scenes = Array.from(root.querySelectorAll('[data-dg-story-scene]'));
    const chapters = Array.from(root.querySelectorAll('[data-dg-story-chapter]'));
    const truthCards = Array.from(root.querySelectorAll('[data-dg-story-truth]'));
    const steps = Array.from(root.querySelectorAll('[data-dg-story-step]'));
    const videos = Array.from(root.querySelectorAll('[data-dg-story-scene-video]'));
    const count = Math.min(scenes.length, chapters.length);
    if (!count) return;

    const story = { root, scenes, chapters, truthCards, steps, videos, count, activeIndex: -1, inView: false };
    stories.add(story);
    if (root.getAttribute('dir') === 'rtl') document.documentElement.classList.add('dg-home-story-rtl');
    root.classList.add('is-story-ready');

    story.videos.forEach((video) => {
      video.pause();
      video.addEventListener('loadedmetadata', () => {
        const progress = Number(video.dataset.scrubProgress || 0);
        scrubVideo(video, Number.isFinite(progress) ? progress : 0);
      });
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        story.inView = entry.isIntersecting;
        if (!entry.isIntersecting) {
          story.videos.forEach((video) => video.pause());
        } else {
          requestUpdate();
        }
      });
    }, { rootMargin: '45% 0px 45% 0px', threshold: 0.01 });

    observer.observe(root);
    setStoryIndex(story, 0);
    updateStory(story);
  };

  const prepareConciergeVideo = (video) => {
    if (!video || video.dataset.conciergePrepared === 'true') return;
    video.dataset.conciergePrepared = 'true';
    const root = video.closest('.dg-home-concierge-v3');
    const soundButton = root && root.querySelector('[data-dg-concierge-sound]');

    const setSoundState = (soundOn) => {
      video.muted = !soundOn;
      video.loop = !soundOn;
      if (!soundButton) return;
      const label = soundOn ? soundButton.dataset.labelMute : soundButton.dataset.labelUnmute;
      soundButton.classList.toggle('is-sound-on', soundOn);
      soundButton.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
      if (label) {
        soundButton.setAttribute('aria-label', label);
        soundButton.title = label;
      }
    };

    setSoundState(false);

    if (reducedMotion.matches || saveData) {
      if (soundButton) soundButton.hidden = true;
      return;
    }

    if (soundButton) {
      soundButton.addEventListener('click', () => {
        const soundOn = video.muted;
        hydrateVideo(video);
        setSoundState(soundOn);
        if (!soundOn) return;
        const result = video.play();
        if (result && typeof result.catch === 'function') {
          result.catch(() => setSoundState(false));
        }
      });
    }

    video.addEventListener('ended', () => {
      setSoundState(false);
      video.currentTime = 0;
      if (reducedMotion.matches || saveData) return;
      const result = video.play();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          hydrateVideo(video);
          const result = video.play();
          if (result && typeof result.catch === 'function') result.catch(() => {});
        } else {
          video.pause();
          setSoundState(false);
        }
      });
    }, { rootMargin: '20% 0px', threshold: 0.08 });

    observer.observe(video);
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-dg-scroll-story]').forEach(prepareStory);
    scope.querySelectorAll('[data-dg-concierge-video]').forEach(prepareConciergeVideo);
    requestUpdate();
  };

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate);
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
})();
